/**
 * CustomerAuthContext.jsx
 * 
 * Authentication context for marketplace customers.
 * Uses Google OAuth via Emergent Auth for simple sign-in.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check if user is authenticated
  // Note: 401 is expected for unauthenticated users, so we don't log it as an error
  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      } else {
        // 401 is expected for unauthenticated users - don't log as error
        setCustomer(null);
      }
    } catch (error) {
      // Network errors or JSON parsing errors
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  // Start Google OAuth flow
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/my-trips/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Process OAuth callback
  const processAuthCallback = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Set customer data immediately for fast navigation
        setCustomer(data.customer);
        
        // Also trigger a fresh fetch from /auth/me to ensure we have all fields
        // This runs in background and will update the customer state with fresh stats
        setTimeout(() => checkAuthStatus(), 100);
        
        return { success: true, customer: data.customer };
      } else {
        const error = await response.json();
        return { success: false, message: error.detail };
      }
    } catch (error) {
      console.error('Error processing auth callback:', error);
      return { success: false, message: 'Authentication failed' };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/customer/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setCustomer(null);
    }
  };

  // Update profile
  const updateProfile = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/customer/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        const result = await response.json();
        setCustomer(prev => ({ ...prev, ...result.customer }));
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, message: error.detail };
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, message: 'Failed to update profile' };
    }
  };

  const value = {
    customer,
    loading,
    isAuthenticated: !!customer,
    loginWithGoogle,
    processAuthCallback,
    logout,
    updateProfile,
    refreshProfile: checkAuthStatus,
    refreshAuth: checkAuthStatus  // Alias for MagicLinkCallback
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export default CustomerAuthProvider;
